import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import {
  isConfirmedReportCode,
  mergeReportDefinition,
  REPORT_PREVIEW_MAX_ROWS,
} from '../config/reportCatalogue';
import type {
  ReportAccess,
  ReportDefinition,
  ReportFilterValue,
  ReportOutputFormat,
  ReportPreview,
  ReportPreviewColumn,
  ReportPreviewRow,
  SafeReportFilterState,
} from '../types/report';
import { formatAttendanceReportDateLabel } from '../utils/attendanceReport';
import { calendarDateFromApi } from '../utils/dateTime';
import { buildReportRequest } from '../utils/reportFilters';
import { downloadReport } from './reportDownloadService';

type Envelope<T> = {
  success?: boolean;
  message?: string;
  data?: T;
};

type ApiReportItem = {
  reportCode?: unknown;
  reportName?: unknown;
  category?: unknown;
  supportedFormats?: unknown;
};

type ApiRunData = {
  report?: {
    reportCode?: unknown;
    reportName?: unknown;
    generatedAt?: unknown;
    filters?: unknown;
  };
  columns?: unknown;
  rows?: unknown;
  summary?: unknown;
};

function createError(message: string, code: string): Error {
  return Object.assign(new Error(message), { code });
}

function textOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

const EMPLOYEE_NAME_KEYS = new Set([
  'displayName',
  'employeeName',
  'employeeFullName',
  'fullName',
]);

function humanizeColumnKey(key: string): string {
  if (EMPLOYEE_NAME_KEYS.has(key)) return 'Employee Name';
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

/** Show the employee name ahead of the employee number column. */
function orderColumnKeys(keys: string[]): string[] {
  const nameKey = keys.find(key => EMPLOYEE_NAME_KEYS.has(key));
  if (!nameKey) return keys;
  const rest = keys.filter(key => key !== nameKey);
  const numberIndex = rest.findIndex(
    key => key === 'employeeNumber' || key === 'employeeCode',
  );
  if (numberIndex < 0) return [nameKey, ...rest];
  const ordered = [...rest];
  ordered.splice(numberIndex, 0, nameKey);
  return ordered;
}

function normalizeFormats(value: unknown): ReportOutputFormat[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<ReportOutputFormat>();
  for (const item of value) {
    if (typeof item !== 'string') continue;
    const format = item.trim().toUpperCase();
    if (format === 'JSON' || format === 'CSV' || format === 'XLSX') {
      seen.add(format);
    }
  }
  return [...seen];
}

function normalizePreviewRows(
  columns: string[],
  rows: unknown[],
  summary: Record<string, unknown> | null,
  maxRows = REPORT_PREVIEW_MAX_ROWS,
): { rows: ReportPreviewRow[]; totalRows: number; truncated: boolean } {
  const sourceRows: Record<string, unknown>[] = [];

  if (rows.length === 0 && summary && Object.keys(summary).length > 0) {
    sourceRows.push(summary);
  } else {
    for (const row of rows) {
      if (row && typeof row === 'object' && !Array.isArray(row)) {
        sourceRows.push(row as Record<string, unknown>);
      }
    }
  }

  const totalRows = sourceRows.length;
  const limit = Math.max(1, maxRows);
  const truncated = totalRows > limit;
  const limited = sourceRows.slice(0, limit);
  const columnKeys = columns.length
    ? columns
    : limited[0]
      ? Object.keys(limited[0])
      : [];

  const previewRows: ReportPreviewRow[] = limited.map((row, index) => {
    const values: Record<string, unknown> = {};
    for (const key of columnKeys) {
      values[key] = row[key] ?? null;
    }
    return {
      rowKey: `preview-${index}`,
      values,
    };
  });

  return { rows: previewRows, totalRows, truncated };
}

/**
 * Attendance Date is a calendar day. An ISO timestamp such as
 * 2026-10-04T18:30:00.000Z is 5 Oct in the business timezone; slicing the UTC
 * date would show 4 Oct. Plain YYYY-MM-DD values are left unchanged.
 */
function displayAttendanceDate(value: unknown): string | null {
  const iso = calendarDateFromApi(value);
  if (!iso) return null;
  return formatAttendanceReportDateLabel(iso);
}

function withDisplayedAttendanceDates(rows: ReportPreviewRow[]): ReportPreviewRow[] {
  return rows.map(row => {
    if (!Object.prototype.hasOwnProperty.call(row.values, 'attendanceDate')) return row;
    return {
      ...row,
      values: {
        ...row.values,
        attendanceDate: displayAttendanceDate(row.values.attendanceDate),
      },
    };
  });
}

function normalizeColumns(value: unknown, rows: ReportPreviewRow[]): ReportPreviewColumn[] {
  const keys: string[] = [];
  if (Array.isArray(value)) {
    for (const item of value) {
      if (typeof item === 'string' && item.trim()) keys.push(item.trim());
    }
  }
  if (!keys.length && rows[0]) {
    keys.push(...Object.keys(rows[0].values));
  }
  return orderColumnKeys(keys).map(key => ({
    key,
    label: humanizeColumnKey(key),
  }));
}

function normalizeAccess(value: unknown): ReportAccess {
  if (!value || typeof value !== 'object') {
    return { restricted: false, reportCodes: [] };
  }
  const { restricted, reportCodes } = value as {
    restricted?: unknown;
    reportCodes?: unknown;
  };
  return {
    restricted: restricted === true,
    reportCodes: Array.isArray(reportCodes)
      ? reportCodes
          .map(code => textOrNull(code)?.toUpperCase())
          .filter((code): code is string => Boolean(code))
      : [],
  };
}

/**
 * Load permitted report definitions and role report access from GET /reports.
 * Unknown codes from the API are dropped; procedure names are never exposed.
 */
export async function getReportCatalogue(): Promise<{
  definitions: ReportDefinition[];
  access: ReportAccess;
}> {
  const response = await apiClient.get<
    Envelope<{ items?: ApiReportItem[]; access?: unknown }>
  >(API_ENDPOINTS.REPORTS.DEFINITIONS);

  const access = normalizeAccess(response.data?.data?.access);
  const items = response.data?.data?.items;
  if (!Array.isArray(items)) return { definitions: [], access };

  const seen = new Set<string>();
  const definitions: ReportDefinition[] = [];

  for (const item of items) {
    const reportCode = textOrNull(item.reportCode)?.toUpperCase();
    if (!reportCode || !isConfirmedReportCode(reportCode)) {
      if (__DEV__ && reportCode) {
        console.warn('[reports] Ignoring unknown report code from API');
      }
      continue;
    }
    if (seen.has(reportCode)) continue;
    seen.add(reportCode);

    const merged = mergeReportDefinition(
      reportCode,
      textOrNull(item.reportName) || reportCode,
      textOrNull(item.category),
      normalizeFormats(item.supportedFormats),
    );
    if (merged) definitions.push(merged);
  }

  return { definitions, access };
}

export async function getReportDefinitions(): Promise<ReportDefinition[]> {
  return (await getReportCatalogue()).definitions;
}

export async function runReportPreview(input: {
  reportCode: string;
  filters: SafeReportFilterState;
  maxRows?: number;
}): Promise<ReportPreview> {
  if (!isConfirmedReportCode(input.reportCode)) {
    throw createError('Report code is not supported', 'INVALID_REPORT_CODE');
  }

  const body = buildReportRequest(input.reportCode, input.filters, 'JSON');
  const maxRows = input.maxRows ?? REPORT_PREVIEW_MAX_ROWS;

  // Guard: never send identity or SQL fields.
  const payload = JSON.stringify(body);
  if (/organizationId|procedureName|"query"|filePath|createdBy/i.test(payload)) {
    throw createError('Unsupported filter fields were provided', 'VALIDATION_ERROR');
  }

  const response = await apiClient.post<Envelope<ApiRunData>>(
    API_ENDPOINTS.REPORTS.RUN(input.reportCode),
    body,
  );

  return toReportPreview(response.data?.data, input.reportCode, body.filters, maxRows);
}

function toReportPreview(
  data: ApiRunData | undefined,
  fallbackReportCode: string,
  fallbackFilters: Record<string, unknown>,
  maxRows: number,
): ReportPreview {
  if (!data) {
    throw createError('The report could not be generated.', 'REPORT_GENERATION_FAILED');
  }

  const rawRows = Array.isArray(data.rows) ? data.rows : [];
  const summary =
    data.summary && typeof data.summary === 'object' && !Array.isArray(data.summary)
      ? (data.summary as Record<string, unknown>)
      : null;

  const columnKeys = Array.isArray(data.columns)
    ? data.columns.filter((item): item is string => typeof item === 'string')
    : [];

  const normalized = normalizePreviewRows(columnKeys, rawRows, summary, maxRows);
  const reportCode = textOrNull(data.report?.reportCode) || fallbackReportCode;
  const rows =
    reportCode === 'LATE_ARRIVAL_DEDUCTIONS'
      ? withDisplayedAttendanceDates(normalized.rows)
      : normalized.rows;
  const columns = normalizeColumns(columnKeys, rows);

  return {
    reportCode,
    reportName: textOrNull(data.report?.reportName) || fallbackReportCode,
    generatedAt: textOrNull(data.report?.generatedAt),
    filters:
      data.report?.filters &&
      typeof data.report.filters === 'object' &&
      !Array.isArray(data.report.filters)
        ? (data.report.filters as Record<string, unknown>)
        : fallbackFilters,
    columns,
    rows,
    summary,
    totalRows: normalized.totalRows,
    page: 1,
    pageSize: maxRows,
    truncated: normalized.truncated,
  };
}

/**
 * Team Lead daily attendance register. Same row shape as the
 * ATTENDANCE_REGISTER report; the backend returns only the signed-in
 * team lead's assigned employees.
 */
export async function runTeamAttendanceRegisterPreview(input: {
  fromDate: string;
  toDate: string;
  maxRows?: number;
}): Promise<ReportPreview> {
  const maxRows = input.maxRows ?? REPORT_PREVIEW_MAX_ROWS;
  const filters = { fromDate: input.fromDate, toDate: input.toDate };
  const response = await apiClient.get<Envelope<ApiRunData>>(
    API_ENDPOINTS.TEAM_DASHBOARD.ATTENDANCE_REGISTER,
    { params: filters },
  );
  return toReportPreview(
    response.data?.data,
    'ATTENDANCE_REGISTER',
    filters,
    maxRows,
  );
}

export async function exportReportCsv(input: {
  reportCode: string;
  reportName?: string;
  filters: Record<string, ReportFilterValue>;
  signal?: AbortSignal;
}) {
  return downloadReport({
    ...input,
    format: 'CSV',
  });
}

export async function exportReportXlsx(input: {
  reportCode: string;
  reportName?: string;
  filters: Record<string, ReportFilterValue>;
  signal?: AbortSignal;
}) {
  return downloadReport({
    ...input,
    format: 'XLSX',
  });
}

/**
 * Execution history APIs are not mounted on the backend.
 * These stubs reject so callers cannot invent history flows.
 */
export async function getReportExecutions(): Promise<never> {
  throw createError(
    'Report execution history is not available.',
    'REPORT_EXECUTION_NOT_FOUND',
  );
}

export async function getReportExecutionDetails(): Promise<never> {
  throw createError(
    'Report execution history is not available.',
    'REPORT_EXECUTION_NOT_FOUND',
  );
}

export async function downloadPreviousExecution(): Promise<never> {
  throw createError(
    'Previous report download is not available.',
    'REPORT_FILE_NOT_AVAILABLE',
  );
}
